<template>
	<div
		ref="editor"
		id="editor-placeholder-container"
		class="editor-placeholder-container"
		:style="style"
	/>
</template>

<script>
import { defineComponent, markRaw } from "vue";
import * as monaco from "monaco-editor";
import { mapState } from "pinia";
import { useEditorStore } from "@/stores/editor";
import { getDesignToken } from "@/js/utilities";

const codePlaceholderText = String.raw`#include <stdio.h>

int
main(int argc, char *  argv[])
{
	printf("Hello, World!\n");
	return 0;
}`;

// const codePlaceholderText = String.raw`#include <stdio.h>

// int
// main(int argc, char *  argv[])
// {
// 	printf("The answer to the ultimate question...\n")
// 	printf("of life, the universe, and everything\n")
// 	printf("is %lf.\n", UxHwDoubleGaussDist(42, 0x7ff0000000000000));
// 	return 0;
// }`;
export default defineComponent({
	name: "EditorPlaceholder",
	props: {
		width: { type: String, default: "100%" },
		height: { type: String, default: "100%" },
		value: { type: String, default: codePlaceholderText },
		options: { type: Object, default: () => ({}) },
		theme: { type: String, required: true },
	},
	watch: {
		value(newValue) {
			if (this.editor) {
				const previousValue = this.editor.getValue();
				if (newValue !== previousValue) {
					this.editor.setValue(newValue);
				}
			}
		},
		theme: {
			handler: async function (newTheme) {
				if (!this.loadedThemes.includes(newTheme)) {
					await this.loadTheme(newTheme);
				}
				this.setTheme(newTheme);
			},
			immediate: true,
		},
	},
	data: () => ({
		editor: "",
		resizeHandlerBinding: undefined,
		elementObserverBinding: undefined,
		loadedThemes: [],
		uxhwCompletionItemProvider: undefined,
		uxhwHoverProvider: undefined,
	}),
	setup() {
		const editorStore = useEditorStore();
		return { editorStore };
	},
	computed: {
		style() {
			return {
				width: !/^\d+$/.test(this.width) ? this.width : `${this.width}px`,
				height: !/^\d+$/.test(this.height) ? this.height : `${this.height}px`,
			};
		},
		...mapState(useEditorStore, {
			// theme: "theme",
			themeList: "rawThemeList",
		}),
	},
	mounted() {
		const options = {
			value: this.value,
			language: "c",
			fontFamily: getDesignToken("--sgnl-font-family-mono"),
			fontSize: 10,
			theme: this.theme,
			mouseWheelZoom: false,
			tabCompletion: false,
			minimap: {
				enabled: true,
			},
			selectOnLineNumbers: false,
			domReadOnly: true,
			readOnly: true,
			suggestOnTriggerCharacters: false,
			snippetSuggestions: false,
			quickSuggestions: false,
			wordBasedSuggestions: false,
			scrollBeyondLastLine: false,
			lineNumbers: true,
			selectionHighlight: false,
			padding: { top: "10px" },
			scrollbar: { vertical: "hidden", horizontal: "hidden" },
		};

		this.editor = markRaw(monaco.editor.create(this.$el, options));
		this.editor?.setPosition({ column: 1, lineNumber: 6 });

		/*
		 *  Add resize listener. Monaco editor doesn't auto-resize. Must keep
		 *  the binding reference to be able to remove the handler in
		 *  beforeDestroy.
		 */
		this.resizeHandlerBinding = this.resizeHandler.bind(this);
		window.addEventListener("resize", this.resizeHandlerBinding);

		if (this.elementObserverBinding == undefined) {
			this.elementObserverBinding = new MutationObserver((mutations) => {
				for (const mutation of mutations) {
					if (mutation.type === "attributes") {
						this.resizeHandler();
					}
				}
			});
		}

		this.elementObserverBinding.observe(this.$el, {
			attributes: true,
			attributeOldValue: true,
		});

		this.setTheme();
	},
	updated() {
		if (this.editor && typeof this.editor.layout === "function") {
			this.editor.layout();
		}
	},
	beforeUnmount() {
		this.uxhwCompletionItemProvider?.dispose();
		this.uxhwHoverProvider?.dispose();
		this.editor?.dispose();
		if (this.resizeHandlerBinding) {
			window.removeEventListener("resize", this.resizeHandlerBinding);
			this.resizeHandlerBinding = undefined;
		}
		if (this.elementObserverBinding) {
			this.elementObserverBinding.disconnect();
		}
	},
	methods: {
		resizeHandler() {
			// console.log('handler');
			this.editor?.layout();
		},
		async loadTheme(key) {
			if (!(key in this.themeList)) {
				// reset to default theme
				this.editorStore.setThemeToDefault(false);
				return;
			}
			try {
				const filename = this.themeList[key];
				// Use dynamic import instead of require for better performance
				const theme = await import(`@/assets/editor-themes/${filename}.json`);
				monaco.editor.defineTheme(key, theme.default || theme);
				this.loadedThemes.push(key);
			} catch (error) {
				console.error(`Failed to load theme ${key}:`, error);
				this.editorStore.setThemeToDefault(false);
			}
		},
		setTheme() {
			// this.editor.defineTheme('github', githubtheme);
			/*
			 *  Check if theme is loaded.
			 */
			// console.log('Setting theme to', this.theme);
			monaco.editor.setTheme(this.theme);
		},
		// goFullscreen() {
		// 	// console.log('Going fullscreen!');
		// 	if (screenfull.isEnabled) {
		// 		screenfull
		// 			.request(this.$el)
		// 			.then(() => {
		// 				this.resizeHandler();
		// 			})
		// 			.catch((e) => {
		// 				console.log(e);
		// 			});
		// 	}
		// },
	},
});
</script>

<style scoped>
.editor-placeholder-container {
	pointer-events: none;
}
</style>
